import React from "react";
import PropTypes from "prop-types";
import { Schema } from "@sparefoot/react-commons";
import { get } from "lodash";

const DAY_MAP = {
	monday: "Monday",
	tuesday: "Tuesday",
	wednesday: "Wednesday",
	thursday: "Thursday",
	friday: "Friday",
	saturday: "Saturday",
	sunday: "Sunday"
};

const buildAmenityFeatures = (storageUnits) => {
	if (!storageUnits?.length) return [];

	const seen = new Set();
	const features = [];
	storageUnits.forEach((unit) => {
		(unit.availableAmenities || []).forEach((amenity) => {
			if (!seen.has(amenity)) {
				seen.add(amenity);
				features.push({
					"@type": "LocationFeatureSpecification",
					name: amenity,
					value: "true"
				});
			}
		});
	});
	return features;
};

const buildOpeningHoursSpecification = (hours) => {
	if (!hours?.office) return [];

	const grouped = {};
	Object.entries(hours.office).forEach(([day, hour]) => {
		const mappedDay = DAY_MAP[day];
		if (!mappedDay) return;

		const isClosedDay =
			!hour?.open ||
			!hour?.closed ||
			typeof hour.open !== "string" ||
			typeof hour.closed !== "string" ||
			hour.open === "-" ||
			hour.closed === "-";

		const opens = isClosedDay ? "0:00" : hour.open.slice(0, 5);
		const closes = isClosedDay ? "0:00" : hour.closed.slice(0, 5);
		const key = `${opens}_${closes}`;

		if (!grouped[key]) {
			grouped[key] = {
				opens,
				closes,
				days: []
			};
		}
		grouped[key].days.push(mappedDay);
	});

	return Object.values(grouped).map(({ opens, closes, days }) => ({
		"@type": "OpeningHoursSpecification",
		dayOfWeek: days,
		opens,
		closes
	}));
};

const buildImages = (images) => {
	if (!images?.length) return [];
	return images.map((url) => ({
		"@type": "ImageObject",
		url
	}));
};

const buildPaymentAccepted = (featuresDataByCategory) => {
	const items = featuresDataByCategory?.payments?.items;
	if (!items?.length) return undefined;
	const accepted = items
		.filter((item) => item.hasAttribute === true)
		.map((item) => item.label);
	return accepted.length ? accepted : undefined;
};

const FacilitySelfStorageSchema = ({
	facility,
	phone,
	baseUrl,
	units,
	images
}) => {
	const rootUrl = baseUrl || "https://www.storage.com";
	const paymentAccepted = buildPaymentAccepted(
		facility?.featuresDataByCategory
	);

	const facilityFullUrl = facility?.url ? `${rootUrl}${facility.url}` : rootUrl;

	const selfStorageSchema = {
		"@context": "https://schema.org",
		"@type": "SelfStorage",
		"@id": `${facilityFullUrl}#selfstorage`,
		name: facility?.name,
		url: facilityFullUrl,
		address: {
			"@type": "PostalAddress",
			streetAddress: `${facility?.location?.streetAddress1 || ""}${facility?.location?.streetAddress2 ? `, ${facility.location.streetAddress2}` : ""}`,
			addressLocality: facility?.location?.city,
			addressRegion: facility?.location?.state,
			postalCode: facility?.location?.zip,
			addressCountry: "US"
		},
		geo: {
			"@type": "GeoCoordinates",
			latitude: facility?.location?.latitude,
			longitude: facility?.location?.longitude
		},
		telephone: get(phone, "formattedNumber"),
		...(get(facility, "reviews.average") &&
			get(facility, "reviews.number") && {
				aggregateRating: {
					"@type": "AggregateRating",
					ratingValue: facility.reviews.average,
					ratingCount: facility.reviews.number
				}
			}),
		priceRange: "$$",
		...(paymentAccepted && {
			paymentAccepted
		}),
		image: buildImages(images),
		amenityFeature: buildAmenityFeatures(units?.storageUnits),
		openingHoursSpecification: buildOpeningHoursSpecification(
			facility?.hours
		),
		...(facility?.location?.latitude &&
			facility?.location?.longitude && {
				hasMap: `https://www.google.com/maps?q=${facility.location.latitude},${facility.location.longitude}`
			})
	};

	return <Schema schema={selfStorageSchema} />;
};

FacilitySelfStorageSchema.propTypes = {
	facility: PropTypes.object.isRequired,
	phone: PropTypes.object,
	baseUrl: PropTypes.string.isRequired,
	units: PropTypes.shape({
		storageUnits: PropTypes.arrayOf(PropTypes.object)
	}),
	images: PropTypes.arrayOf(PropTypes.string)
};

export default FacilitySelfStorageSchema;
